<template>
	<footer class="isolate mt-8 md:mt-20 hover:[&_a]:text-button-link-hover" :class="{'rem:-mt-[90px]': shiftUp}">
		<div :style="backgroundStyles" class="bg-[length:110px] xs:bg-[length:180px] sm:bg-[length:230px] md:bg-contain">
			<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="1 0 413 56" class="md:hidden relative -z-1">
				<path fill="#fff" stroke="#F4F4F4" stroke-width="3" d="M412.781 2C257.826 43.425 146.101 37.441 2 2v52h410.781V2Z" />
				<path fill="#fff" d="M414.28 3C258.194 45.221 145.653 39.123.5 3v53h413.78V3Z" />
			</svg>
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1430 95" fill="none" class="max-md:hidden relative -z-1">
				<path fill="#fff" stroke="#F4F4F4" stroke-width="3" d="M1.5 90V2.9C464.226 113.277 1082.74 89.264 1438 1.916V90H1.5Z" />
				<path fill="#fff" d="M1439.5 3C1084.29 90.786 463.483 115.015 0 4v90.5h1439.5V3Z" />
			</svg>
			<div class="max-md:pt-0 relative px-4 bg-background-primary -z-1">
				<div class="flex justify-center pb-2 -mt-3 xs:-mt-4 md:pb-0 md:-mt-2 xl:-mt-4">
					<AbsoluteInternalLink to="/" aria-label="Přejít na hlavní stránku">
						<NuxtIcon name="custom/globus-claim" filled class="rem:text-[104px] xs:rem:text-[140px] md:rem:text-[200px]" />
					</AbsoluteInternalLink>
				</div>
			</div>
		</div>
		<div class="pt-8 pb-6 bg-background-secondary text-base md:pt-6 md:pb-4">
			<div class="container space-y-11">
				<div v-if="footer" class="grid grid-cols-1 mx-auto md:max-w-full md:flex md:justify-between gap-0 md:gap-12">
					<div class="md:order-2 flex flex-col">
						<h2 v-if="selectedHypermarketInstance" class="typo-h3 text-center text-heading-primary md:text-left mb-4 md:ml-6">
							{{ selectedBranchLabel }} <span class="ml-2 md:ml-4">{{ selectedHypermarketInstance.content.name }}</span>
						</h2>
						<div v-if="selectedHypermarketInstance" class="md:flex md:gap-2">
							<NuxtIcon name="clock" filled class="max-md:hidden text-icon-primary rem:mt-[5px]" />
							<OpeningHoursTable
								v-if="isMounted && selectedHypermarket && hypermarketDetails?.openingHourEntities"
								:entities="hypermarketDetails.openingHourEntities"
								class="w-full [&_tr+tr>th]:pt-2 [&_tr+tr>td]:pt-2 !mr-0"
								th-class="w-7/12"
								linked-content
								linked-content-hypermarket
							/>
						</div>
						<hr v-if="selectedHypermarketInstance" class="max-md:hidden border-border-secondary my-6" />
						<FooterSupport class="max-md:hidden" />
						<hr class="border-border-secondary my-6" />
						<ul class="max-md:hidden h-fit grid grid-cols-1 gap-y-2">
							<li>
								<span class="flex gap-2 items-center">
									<NuxtIcon name="custom/company" filled class="text-icon-primary rotate-270" />
									<LinkBlock :blok="enrichedLinks.career" class="font-normal" />
								</span>
							</li>
							<li>
								<LinkBlock class="md:ml-6 font-normal" :blok="enrichedLinks.about" />
							</li>
						</ul>
					</div>
					<div class="md:order-1">
						<h2 class="typo-h3 text-center text-heading-primary md:text-left mb-4">
							Důležité odkazy
						</h2>
						<ul class="grid grid-cols-2 gap-y-2 gap-x-4 md:grid-cols-1 md:mb-4">
							<li v-for="link in primaryLinksWithSelectedHypermarket" :key="link.value.id">
								<LinkBlock :blok="link" :link="link.value.cached_url" class="font-normal" />
							</li>
						</ul>
						<FooterMobileAppLinks class="hidden md:block" />
					</div>
					<hr class="md:hidden border-border-secondary my-6" />
					<div class="grid grid-cols-2 gap-x-4 md:hidden">
						<ul class="h-fit grid grid-cols-1 gap-y-2">
							<li v-for="link in footer.content.secondaryLinkBlock" :key="link.value.id">
								<LinkBlock :blok="link" class="font-normal" />
							</li>
						</ul>
						<ul class="h-fit grid grid-cols-1 gap-y-2">
							<li>
								<LinkBlock :blok="enrichedLinks.career" class="font-normal" />
							</li>
							<li>
								<LinkBlock :blok="enrichedLinks.about" class="font-normal" />
							</li>
						</ul>
					</div>
					<hr class="md:hidden border-border-secondary my-6" />
					<FooterMobileAppLinks class="block md:hidden" />
					<hr class="md:hidden border-border-secondary my-6" />
					<FooterSupport class="md:hidden" />
					<hr class="md:hidden border-border-secondary my-6" />
					<div class="md:order-4">
						<h2 class="typo-h3 text-center text-heading-primary md:text-left mb-4">
							Sledujte nás
						</h2>
						<ul class="flex justify-evenly md:flex-col max-md:flex-wrap md:justify-start gap-3 md:mb-4">
							<li>
								<a :href="footer.content.facebook" target="_blank" class="flex gap-2 items-center">
									<NuxtIcon name="custom/facebook" filled class="text-base md:rem:text-[26px] text-[#1877F2]" />
									Facebook
								</a>
							</li>
							<li>
								<a :href="footer.content.youtube" target="_blank" class="flex gap-2 items-center">
									<NuxtIcon name="custom/youtube" filled class="text-base md:rem:text-[26px]" />
									YouTube
								</a>
							</li>
							<li>
								<a :href="footer.content.instagram" target="_blank" class="flex gap-2 items-center">
									<NuxtPicture
										src="images/instagram.png"
										alt="Instagram logo"
										quality="100"
										width="24"
										height="24"
										sizes="xs:40px sm:40px md:60px lg:60px xl:60px xxl:60px"
										class="rem:w-[16px] md:rem:w-[24px]"
										loading="lazy"
									/>
									Instagram
								</a>
							</li>
						</ul>
						<hr class="max-md:hidden border-border-secondary my-6" />
						<ul class="max-md:hidden h-fit grid grid-cols-1 gap-y-2 md:mt-auto">
							<li v-for="link in footer.content.secondaryLinkBlock" :key="link.value.id">
								<LinkBlock :blok="link" class="font-normal" />
							</li>
						</ul>
					</div>
				</div>
				<div class="text-center typo-body-s font-light">
					&copy; Copyright Globus, {{ new Date().getFullYear() }}
				</div>
			</div>
		</div>
	</footer>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {GLOBAL_BRANCH_SLUG} from '#shared/constants';
	import type {LinkBlockStoryblok} from '~/storyblok/types';
	import {useStoryblokFooterStore} from '~/stores/storyblokFooter';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';

	interface Props {
		shiftUp?: boolean;
	}

	defineProps<Props>();

	const isMounted = useMounted();
	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket, selectedHypermarketInstance} = storeToRefs(hypermarketsStore);
	const selectedBranchLabel = computed(() => selectedHypermarketInstance.value?.branchType === 'fresh' ? 'Můj Fresh' : 'Můj Hypermarket');

	const img = useImage();

	const backgroundStyles = computed(() => {
		const url = img('/images/footer-illustration.svg', undefined, {sizes: 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw xxl:100vw'});
		return {
			backgroundImage: `url('${url}')`,
			backgroundRepeat: 'no-repeat',
			backgroundPosition: 'bottom left',
		};
	});

	const footerStore = useStoryblokFooterStore();
	const {footer} = storeToRefs(footerStore);

	const enrichedLinks = computed<Record<string, LinkBlockStoryblok>>(() => {
		return {
			about: {
				label: 'O nás',
				value: footer.value?.content.aboutLink ?? {},
				_uid: '',
				component: 'LinkBlock',
			},
			career: {
				label: 'Volná pracovní místa',
				value: footer.value?.content.careerLink ?? {},
				_uid: '',
				component: 'LinkBlock',
			},
		};
	});

	const primaryLinksWithSelectedHypermarket = computed(() => {
		const links = footer.value?.content.primaryLinkBlock ?? [];
		const selected = selectedHypermarket.value ?? GLOBAL_BRANCH_SLUG;

		const labelToPathMap: Record<string, string> = {
			'Náše služby': '/sluzby/',
			'Náše služby a produkty': '/sluzby/',
			'Akční nabídka': '/hypermarket/akcni-nabidka/',
			'Online objednávky': '/online-objednavka/',
		};

		return links.map((link: LinkBlockStoryblok) => {
			const cloned = {...link, value: {...link.value}};
			if (labelToPathMap[cloned.label]) {
				cloned.value.url = cloned.value.cached_url = `/${selected}${labelToPathMap[cloned.label]}`;
			}
			return cloned;
		});
	});

	const {data: hypermarketDetails} = useHypermarketDetails(selectedHypermarket);
</script>
